/** The in-widget deposit step.
 *
 * A booking that still owes a deposit is NOT confirmed. The widget used to
 * show "You're all set!" for one (clients closed the tab believing they were
 * booked and lost the slot when the link expired), then briefly bounced them
 * to the standalone self-checkout page — a different screen with a different
 * form, mid-flow. The deposit is now a step of the widget itself: card + zip,
 * a live "held for hh:mm" countdown, and "text me the link" for anyone who
 * wants to pay from their phone.
 *
 * The client record and the booking are created at submit, BEFORE this step:
 * someone who gets this far without their card to hand is a hot lead the spa
 * can still reach, not a lost form.
 *
 * Widget bookings are written through a queue, so in production POST
 * /bookings/ answers 202 before the booking (and its payment link) exists.
 * The link is therefore collected from the pending endpoint the same way the
 * quick-book flow does it — reading it off the 202 only works in dev, where
 * Huey runs inline.
 */

/** Fallback agreement wording when the business hasn't customized it. */
export const DEFAULT_GIFT_CARD_AGREEMENT =
  'I agree that if my gift card is valid and has enough balance on it, it will be used ' +
  'for the deposit; otherwise the deposit — or whatever remains after the gift card is ' +
  "used up — will be charged to the card I'm providing.";

export interface DepositLink {
  token: string;
  amount: number;
  /** ISO instant the hold lapses (booking auto-cancels). */
  expiresAt: string;
  /** Appointment starts inside the hold window: pay now, no "text me later". */
  neededNow: boolean;
  /** How long the automatic reminder text is held for (minutes). */
  reminderDelayMinutes: number;
  /** ISO instant the client already asked to be texted, if they did. */
  textedAt: string | null;
  payUrl: string;
  /** Cancellation policy for THIS payer: required (no agreement on record —
   *  portal bookings, which skip the contact step's toggle), the text, or a
   *  receipt timestamp. Online bookings arrive stamped → required false. */
  policy?: { required: boolean; html: string; agreedAt: string | null };
  /** 'deposit' (default) holds the appointment; 'products' is the OPTIONAL
   *  pay-now for a shop-cart pickup — nothing lapses if it is skipped. */
  kind?: 'deposit' | 'products';
  subtotal?: number;
  tax?: number;
}

/** The pay-now link for a shop-cart pickup's products (server: retail_prepay_fields). */
export interface ProductPrepayCarrier {
  paymentLinkToken?: string;
  amount?: number | string;
  subtotal?: number | string | null;
  tax?: number | string | null;
  payUrl?: string;
  payExpiresAt?: string | null;
}

export function productPrepayLink(pp: ProductPrepayCarrier | null | undefined): DepositLink | null {
  if (!pp || !pp.paymentLinkToken || !(Number(pp.amount) > 0)) return null;
  return {
    token: pp.paymentLinkToken,
    amount: Number(pp.amount),
    expiresAt: '',                 // no hold: skipping it changes nothing
    neededNow: false,
    reminderDelayMinutes: 0,
    textedAt: null,
    payUrl: pp.payUrl || '',
    policy: { required: false, html: '', agreedAt: null },
    kind: 'products',
    subtotal: Number(pp.subtotal ?? pp.amount),
    tax: Number(pp.tax ?? 0),
  };
}

interface DepositCarrier {
  status?: string;
  bookingStatus?: string;
  paymentLinkToken?: string;
  depositAmount?: number | string | null;
  payExpiresAt?: string | null;
  depositNeededNow?: boolean;
  reminderDelayMinutes?: number;
  clientTextedAt?: string | null;
  payUrl?: string;
  productPrepay?: ProductPrepayCarrier;
}

export interface BookingSubmitResult extends DepositCarrier {
  pendingToken?: string;
}

export interface PendingOutcome extends DepositCarrier {
  status?: 'pending' | 'completed' | 'failed';
  error?: string;
  /** Entitlements-only device token — see storeRecognition. The widget's POST
   *  gets a 202, so the token can only arrive with the completed outcome. */
  recognitionToken?: { token: string; clientId: number; expiresInDays?: number };
}

function _fromCarrier(c: DepositCarrier | null | undefined): DepositLink | null {
  if (!c || !c.paymentLinkToken) return null;
  return {
    token: c.paymentLinkToken,
    amount: Number(c.depositAmount || 0),
    expiresAt: c.payExpiresAt || '',
    neededNow: !!c.depositNeededNow,
    reminderDelayMinutes: Number(c.reminderDelayMinutes ?? 20),
    textedAt: c.clientTextedAt || null,
    payUrl: c.payUrl || '',
    policy: (c as any).policy || { required: false, html: '', agreedAt: null },
  };
}

/** The deposit link carried directly on a booking response, or null. */
export function depositFromResponse(result: BookingSubmitResult | null | undefined): DepositLink | null {
  if (!result) return null;
  if (result.status !== 'pending_deposit') return productPrepayLink(result.productPrepay);
  return _fromCarrier(result);
}

/** The deposit link from a completed pending-booking outcome, or null. */
export function depositFromPending(p: PendingOutcome | null | undefined): DepositLink | null {
  if (!p || p.status !== 'completed') return null;
  if (p.bookingStatus !== 'pending_deposit') return productPrepayLink(p.productPrepay);
  return _fromCarrier(p);
}

export interface AwaitOpts {
  fetchPending: (token: string) => Promise<PendingOutcome>;
  sleep?: (ms: number) => Promise<void>;
  maxPolls?: number;
  intervalMs?: number;
  /** Fired once with the completed outcome, deposit or not — the caller's one
   *  chance to read completion extras (recognitionToken) off the poll. */
  onCompleted?: (outcome: PendingOutcome) => void;
}

/** Resolve the deposit link for a just-submitted booking, or null.
 *
 * Waits only as long as it takes the queue to write the booking. If that
 * takes too long we return null and the caller shows the confirmation
 * screen — the deposit text/email still goes out, so a slow queue degrades
 * to the old behaviour instead of hanging the client on a spinner.
 */
export async function awaitDepositLink(
  result: BookingSubmitResult | null | undefined,
  opts: AwaitOpts,
): Promise<DepositLink | null> {
  const direct = depositFromResponse(result);
  if (direct) return direct;

  const token = result && result.pendingToken;
  if (!token) return null;

  const sleep = opts.sleep || ((ms: number) => new Promise<void>(r => setTimeout(r, ms)));
  const maxPolls = opts.maxPolls ?? 8;
  const intervalMs = opts.intervalMs ?? 1200;

  for (let i = 0; i < maxPolls; i++) {
    let outcome: PendingOutcome | null = null;
    try {
      outcome = await opts.fetchPending(token);
    } catch {
      // Transient — keep waiting; a network blip shouldn't strand the client.
    }
    if (outcome && outcome.status === 'completed') {
      try { opts.onCompleted?.(outcome); } catch { /* extras never block the flow */ }
      return depositFromPending(outcome);
    }
    // The queue rejected the booking (slot taken meanwhile, staffing rule…):
    // there is no appointment, so "You're all set" would be a lie.
    if (outcome && outcome.status === 'failed') throw new Error(outcome.error || 'Booking could not be completed. Please try again.');
    if (i < maxPolls - 1) await sleep(intervalMs);
  }
  return null;
}

/** Seconds left on the hold, never negative. */
export function holdSecondsLeft(expiresAt: string, now: number = Date.now()): number {
  const t = Date.parse(expiresAt);
  if (!expiresAt || Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((t - now) / 1000));
}

/** "hh:mm" (or "mm:ss" under an hour, so the last minutes visibly move). */
export function holdCountdown(expiresAt: string, now: number = Date.now()): string {
  const secs = holdSecondsLeft(expiresAt, now);
  const pad = (n: number) => String(n).padStart(2, '0');
  if (secs >= 3600) {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return `${pad(h)}:${pad(m)}`;
  }
  return `${pad(Math.floor(secs / 60))}:${pad(secs % 60)}`;
}

/** Plain-English "in 20 minutes" / "in about an hour" for the later-text copy. */
export function minutesPhrase(mins: number): string {
  if (mins <= 0) return 'shortly';
  if (mins === 1) return 'in 1 minute';
  if (mins < 60) return `in ${mins} minutes`;
  const h = Math.round(mins / 60);
  return h === 1 ? 'in about an hour' : `in about ${h} hours`;
}
